import { FormApi, FormStateBasic } from 'informed';

import { productFormsMachineEvents } from '../../../product/constants/formMachineConstants';
import {
  AssignConfigEventToContext,
  CallEventsFromConfig,
  SetGoToNextSteps
} from '../../../product/machines/PublicFormsMachine/types';
import {
  PublicFormMachineSendUnion,
  PublicFormMachineStateUnion
} from '../../types/MultiStepRegistrationFormTypes';
import { isAuthenticationStep } from '../../utils/MultiStepSuccessStepUtils';

interface UseMultiStepNavigationParams {
  formApi: FormApi;
  formValues: FormStateBasic['values'];
  publicFormMachineState: PublicFormMachineStateUnion;
  publicFormMachineSend: PublicFormMachineSendUnion;
  currentStepType: string;
  stepId: string;
  nextBtnActions?: string[];
  prevBtnActions?: string[];
  next: () => void;
  back: () => void;
  onValidatedBeforeStepActions?: (params: {
    formValues: Record<string, unknown>;
    stepId: string;
    proceed: () => void;
    advanceToNextStep: () => void;
  }) => void;
}

export const useMultiStepNavigation = ({
  formApi,
  formValues,
  publicFormMachineState,
  publicFormMachineSend,
  currentStepType,
  stepId,
  nextBtnActions,
  prevBtnActions,
  next,
  back,
  onValidatedBeforeStepActions
}: UseMultiStepNavigationParams) => {
  publicFormMachineState.context.back = back;
  publicFormMachineState.context.next = next;

  const dispatchConfigBtnActions = (configEvent: string[] | undefined): void => {
    publicFormMachineSend({
      type: productFormsMachineEvents.assignConfigEventsToContext,
      configEvent,
      values: formValues
    } as AssignConfigEventToContext);
    publicFormMachineSend({
      type: productFormsMachineEvents.callEventsFromConfig
    } as CallEventsFromConfig);
  };

  const proceedToNextStep = (): void => {
    dispatchConfigBtnActions(nextBtnActions ?? []);
  };

  const handleValidFormSubmission = (): void => {
    if (onValidatedBeforeStepActions) {
      onValidatedBeforeStepActions({
        formValues: formValues as Record<string, unknown>,
        stepId,
        proceed: proceedToNextStep,
        advanceToNextStep: next
      });
      return;
    }

    proceedToNextStep();
  };

  const tryAdvanceViaGoToNextSteps = (): boolean => {
    const goToNextSteps = publicFormMachineState.context.goToNextSteps;
    if (goToNextSteps.length > 0) {
      publicFormMachineSend({
        type: productFormsMachineEvents.setGoToNextSteps,
        shouldRemove: true
      } as SetGoToNextSteps);
      next();
      return true;
    }

    return false;
  };

  const onClickBackButton = (): void => {
    if (isAuthenticationStep(currentStepType)) {
      formApi.setValue('otp', '');
    }
    dispatchConfigBtnActions(prevBtnActions);
  };

  return {
    onClickBackButton,
    handleValidFormSubmission,
    tryAdvanceViaGoToNextSteps
  };
};
